import React from 'react';
import { COMPONENT_TYPES } from '../../constants';
import { SkinnableComponent } from '../../types/component-skin';
import { createSkinnableWrapper, createSkinnableEditorWrapper } from './registry-helpers';

/**
 * 컴포넌트 정보 인터페이스
 */
interface ComponentInfo {
  /** 컴포넌트 유형 */
  type: string;
  /** 컴포넌트 */
  component: React.ComponentType<any>;
  /** 에디터 모드 컴포넌트 */
  editorComponent?: React.ComponentType<any>;
  /** 컴포넌트 속성 정의 */
  properties?: any[] | React.ComponentType<any>;
  /** 기본 속성 */
  defaultProps?: Record<string, any>;
  /** 카테고리 */
  category?: string;
  /** 아이콘 */
  icon?: string;
  /** 이름 */
  name: string;
  /** 설명 */
  description?: string;
  /** 스킨 가능 컴포넌트 정의 */
  skinnable?: SkinnableComponent;
}

// 컴포넌트 레지스트리
const componentRegistry: Record<string, ComponentInfo> = {};

// 속성 패널 레지스트리
const propertyPanelRegistry: Record<string, React.ComponentType<any>> = {};

/**
 * 컴포넌트 레지스트리에 컴포넌트 등록
 * @param componentInfo 등록할 컴포넌트 정보
 */
export const registerComponent = (componentInfo: ComponentInfo): void => {
  const { type } = componentInfo;
  if (!type) {
    console.error('컴포넌트 유형이 지정되지 않았습니다.');
    return;
  }

  // 컴포넌트 등록
  componentRegistry[type] = componentInfo;
  
};

/**
 * 속성 패널 레지스트리에 속성 패널 등록
 * @param type 컴포넌트 유형
 * @param propertyPanel 속성 패널 컴포넌트
 */
export const registerPropertyPanel = (
  type: string,
  propertyPanel: React.ComponentType<any>
): void => {
  propertyPanelRegistry[type] = propertyPanel;
};

/**
 * 컴포넌트 정보 조회
 * @param type 컴포넌트 유형
 * @returns 컴포넌트 정보
 */
export const getComponentInfo = (type: string): ComponentInfo | undefined => {
  return componentRegistry[type];
};

/**
 * 컴포넌트 조회
 * @param type 컴포넌트 유형
 * @returns 컴포넌트
 */
export const getComponent = (type: string): React.ComponentType<any> | undefined => {
  const info = componentRegistry[type];
  return info?.component;
};

/**
 * 에디터 컴포넌트 조회
 * @param type 컴포넌트 유형
 * @returns 에디터 컴포넌트
 */
export const getEditorComponent = (type: string): React.ComponentType<any> | undefined => {
  const info = componentRegistry[type];
  return info?.editorComponent;
};

/**
 * 속성 패널 조회
 * @param type 컴포넌트 유형
 * @returns 속성 패널 컴포넌트
 */
export const getPropertyPanel = (type: string): React.ComponentType<any> | undefined => {
  return propertyPanelRegistry[type];
};

/**
 * 컴포넌트 기본 속성 조회
 * @param type 컴포넌트 유형
 * @returns 기본 속성
 */
export const getDefaultProps = (type: string): Record<string, any> => {
  const info = componentRegistry[type];
  return info?.defaultProps || {};
};

/**
 * 레지스트리에 등록된 모든 컴포넌트 목록 조회
 * @returns 등록된 모든 컴포넌트 정보 목록
 */
export const getAllComponents = (): ComponentInfo[] => {
  return Object.values(componentRegistry);
};

/**
 * 카테고리별 컴포넌트 목록 조회
 * @param category 카테고리
 * @returns 해당 카테고리의 컴포넌트 정보 목록
 */
export const getComponentsByCategory = (category: string): ComponentInfo[] => {
  return Object.values(componentRegistry).filter(info => info.category === category);
};

/**
 * 레거시 컴포넌트 등록 (호환성 지원)
 * @param type 컴포넌트 유형
 * @param config 컴포넌트 설정
 */
export const registerLegacyComponent = (
  type: string,
  config: {
    name: string;
    category?: string;
    icon?: string;
    defaultProps?: Record<string, any>;
  }
): void => {
  // 실제 레거시 컴포넌트 등록 로직 구현
  // ...
};

/**
 * 스킨 가능한 컴포넌트 등록
 * @param skinnable 스킨 가능한 컴포넌트 정의
 */
export const registerSkinnableComponent = (skinnable: SkinnableComponent): void => {
  const { type, name, category, icon, defaultProps } = skinnable;
  
  // Create wrapper components using helper functions
  const SkinnableWrapper = createSkinnableWrapper(skinnable);
  const SkinnableEditorWrapper = createSkinnableEditorWrapper(skinnable);
  
  // Register the component
  registerComponent({
    type,
    name,
    category,
    icon,
    defaultProps,
    component: SkinnableWrapper,
    editorComponent: SkinnableEditorWrapper,
    properties: skinnable.options,
    skinnable
  });
};

/**
 * 컴포넌트가 스킨을 지원하는지 확인
 * @param type 컴포넌트 유형
 * @returns 스킨 지원 여부
 */
export const isSkinnableComponent = (type: string): boolean => {
  const info = componentRegistry[type];
  return !!info?.skinnable;
};

/**
 * 스킨 가능한 컴포넌트 정의 조회
 * @param type 컴포넌트 유형
 * @returns 스킨 가능한 컴포넌트 정의
 */
export const getSkinnableComponent = (type: string): SkinnableComponent | undefined => {
  const info = componentRegistry[type];
  return info?.skinnable;
};